import { useCallback, useEffect, useId, useState } from "react";
import "./DateTextInput.css";

const DEFAULT_PLACEHOLDER = "DD/MM/YYYY";

function getDaysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

function clampNumber(value, min, max) {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
}

function normalizeDateDraft(value) {
  const digits = String(value || "")
    .replace(/\D/g, "")
    .slice(0, 8);

  if (!digits) return "";

  let dayPart = digits.slice(0, 2);
  let monthPart = digits.slice(2, 4);
  let yearPart = digits.slice(4, 8);

  if (dayPart.length === 2) {
    dayPart = String(clampNumber(Number(dayPart), 1, 31)).padStart(2, "0");
  }

  if (monthPart.length === 2) {
    monthPart = String(clampNumber(Number(monthPart), 1, 12)).padStart(2, "0");
  }

  if (yearPart.length === 4) {
    yearPart = String(clampNumber(Number(yearPart), 2000, 2099)).padStart(4, "0");
  }

  let normalized = dayPart;
  if (digits.length > 2) normalized += `/${monthPart}`;
  if (digits.length > 4) normalized += `/${yearPart}`;

  if (dayPart.length === 2 && monthPart.length === 2 && yearPart.length === 4) {
    const maxDay = getDaysInMonth(Number(yearPart), Number(monthPart));
    const clampedDay = String(clampNumber(Number(dayPart), 1, maxDay)).padStart(2, "0");
    normalized = `${clampedDay}/${monthPart}/${yearPart}`;
  }

  return normalized;
}

export function formatIsoDateForInput(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ""));
  if (!match) return "";
  return `${match[3]}/${match[2]}/${match[1]}`;
}

function clampIsoDate(isoValue, minValue, maxValue) {
  let nextValue = isoValue;
  if (minValue && nextValue < minValue) {
    nextValue = minValue;
  }
  if (maxValue && nextValue > maxValue) {
    nextValue = maxValue;
  }
  return nextValue;
}

function parseDisplayDate(value, minValue, maxValue) {
  const trimmedValue = String(value || "").trim();
  if (!trimmedValue) {
    return { empty: true, valid: true, iso: "", display: "" };
  }

  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(trimmedValue);
  if (!match) {
    return { valid: false };
  }

  const year = clampNumber(Number(match[3]), 2000, 2099);
  const month = clampNumber(Number(match[2]), 1, 12);
  const maxDay = getDaysInMonth(year, month);
  const day = clampNumber(Number(match[1]), 1, maxDay);
  const iso = clampIsoDate(
    `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`,
    minValue,
    maxValue,
  );

  return {
    valid: true,
    iso,
    display: formatIsoDateForInput(iso),
  };
}

export default function DateTextInput({
  label,
  value,
  onChange,
  minValue = "",
  maxValue = "",
  placeholder = DEFAULT_PLACEHOLDER,
  className = "",
}) {
  const inputId = useId();
  const [draftValue, setDraftValue] = useState(() => formatIsoDateForInput(value));
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    setDraftValue(formatIsoDateForInput(value));
    setInvalid(false);
  }, [value]);

  const commitValue = useCallback(
    (nextDraftValue, finalize = false) => {
      const parsedValue = parseDisplayDate(nextDraftValue, minValue, maxValue);

      if (parsedValue.empty) {
        setDraftValue("");
        setInvalid(false);
        onChange("");
        return;
      }

      if (!parsedValue.valid) {
        if (finalize) {
          setInvalid(true);
        }
        return;
      }

      setDraftValue(parsedValue.display);
      setInvalid(false);
      onChange(parsedValue.iso);
    },
    [maxValue, minValue, onChange],
  );

  const handleChange = useCallback(
    (rawValue) => {
      const normalizedValue = normalizeDateDraft(rawValue);
      setDraftValue(normalizedValue);
      setInvalid(false);

      if (!normalizedValue) {
        onChange("");
        return;
      }

      if (normalizedValue.length === 10) {
        commitValue(normalizedValue, false);
      }
    },
    [commitValue, onChange],
  );

  return (
    <>
      {label && (
        <label className="admin-label" htmlFor={inputId}>
          {label}
        </label>
      )}
      <input
        id={inputId}
        className={`admin-input date-text-input${invalid ? " date-text-input-invalid" : ""}${className ? ` ${className}` : ""}`}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        spellCheck={false}
        maxLength={10}
        placeholder={placeholder}
        value={draftValue}
        onChange={(event) => handleChange(event.target.value)}
        onBlur={() => commitValue(draftValue, true)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            commitValue(draftValue, true);
          }
        }}
        aria-invalid={invalid}
      />
    </>
  );
}
